/**
 * Dialog for editing existing shifts
 * Includes form validation, conflict detection, and optimistic locking
 * Supports series editing with scope selection for pattern-based shifts
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { TimePicker } from '@/components/ui/time-picker';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DatePicker } from '@/components/ui/date-picker';
import { AlertTriangle, Loader2, RefreshCw, Repeat, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { EmployeeSelect } from './EmployeeSelect';
import { ShiftAvailabilityWarning } from './ShiftAvailabilityWarning';
import { useAllEmployees } from '@/hooks/useEmployees';
import { GroupMultiSelect } from './GroupMultiSelect';
import { useShiftWriteScope } from '@/hooks/useShiftWriteScope';
import { ShiftTypeBadge } from './ShiftTypeBadge';
import { ShiftStatusBadge } from './ShiftStatusBadge';
import {
  SeriesUpdateChoiceDialog,
  type UpdateScope,
} from './SeriesUpdateChoiceDialog';
import {
  RecurrenceSettings,
  RecurrenceType,
  type RecurrenceTypeValue,
} from './RecurrenceSettings';
import { ApiError } from '@/api';
import {
  useUpdateShift,
  useCheckShiftConflicts,
  useShiftPattern,
  useUpdateShiftSeries,
  shiftKeys,
} from '@/hooks/useShifts';
import { ShiftType, ShiftStatus } from '@/types/shifts';
import type { Shift } from '@/types/shifts';
import { shifts_UpdateShiftSeriesRequest } from '@/api/models/shifts_UpdateShiftSeriesRequest';
import { HelpTip } from '@/components/ui/help-tip';
import { focusFirstShiftField } from './dialogFocus';
import { buildSeriesUpdateRequest } from './seriesUpdateRequest';
import { ShiftCollisionAlert, collisionFromError, type ShiftCollision } from './ShiftCollisionAlert';
import { extractBackendError } from '@/lib/translateBackendError';
import { ShiftWarningLines } from './ShiftWarningLines';
import {
  getUpdateShiftSchema,
  toUpdateShiftRequest,
  shiftToFormDefaults,
  OPEN_SHIFT_VALUE,
  type UpdateShiftFormData,

  type UpdateShiftFormInput,
} from '@/schemas/shiftSchemas';

export interface EditShiftDialogProps {
  shift: Shift;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Dialog component for editing existing shifts.
 * Integrates with the useUpdateShift and useCheckShiftConflicts hooks.
 * Handles optimistic locking via the currentUpdatedAt field.
 */
export function EditShiftDialog({
  shift,
  open,
  onOpenChange,
  onSuccess,
}: EditShiftDialogProps) {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  // The dialog shows overlap, absence and lock 409s itself (#2119 F7).
  const updateShift = useUpdateShift({ inlineConflicts: true });
  const updateShiftSeries = useUpdateShiftSeries({ inlineConflicts: true });
  const checkConflicts = useCheckShiftConflicts();
  const { data: employeeList } = useAllEmployees();
  const [optimisticLockError, setOptimisticLockError] = useState(false);
  // The days a save collided on (409 SHIFT_CONFLICT or EMPLOYEE_ABSENT, #2119 F7).
  const [saveCollision, setSaveCollision] = useState<ShiftCollision | null>(null);
  // D3 (#2119): a series shift edits date and employee only on its own. The planner unlocks
  // them with "Change this shift only"; the save then goes through the single-shift update.
  const [singleEdit, setSingleEdit] = useState(false);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<UpdateShiftFormData | null>(
    null
  );

  // Recurrence settings: user overrides (null = use patternData value)
  const [userRecurrenceType, setUserRecurrenceType] = useState<RecurrenceTypeValue | null>(null);
  const [userRepetitions, setUserRepetitions] = useState<number | null>(null);

  // Determine if this is a series shift that can be edited as series
  const isSeriesShift = !!(shift.pattern_id && !shift.is_exception);
  // Series edit mode: date and employee are read-only, the save asks for the scope.
  const seriesMode = isSeriesShift && !singleEdit;

  // Fetch pattern data when editing a series shift
  const { data: patternData, isLoading: patternLoading } = useShiftPattern(
    shift.pattern_id || '',
    { enabled: isSeriesShift && open }
  );

  // Effective recurrence values: user override > patternData > defaults
  // This avoids the useEffect timing gap that caused the Select to render
  // with stale default values before patternData was applied
  const recurrenceType: RecurrenceTypeValue =
    userRecurrenceType ??
    (patternData?.recurrence_type as RecurrenceTypeValue) ??
    RecurrenceType.DAILY;
  const repetitions: number =
    userRepetitions ?? patternData?.repetitions ?? 1;

  // Track original recurrence values to detect changes
  const originalRecurrenceType = patternData?.recurrence_type as
    | RecurrenceTypeValue
    | undefined;
  const originalRepetitions = patternData?.repetitions;

  // Check if recurrence settings have changed
  const hasRecurrenceChanges =
    seriesMode &&
    (recurrenceType !== originalRecurrenceType ||
      repetitions !== originalRepetitions);

  // Form setup with Zod validation
  const schema = useMemo(() => getUpdateShiftSchema(i18n), [i18n]);
  // Group-lead write scope (#2043): a lead moves a shift only between their groups.
  const writeScope = useShiftWriteScope();
  const form = useForm<UpdateShiftFormInput, unknown, UpdateShiftFormData>({
    resolver: zodResolver(schema),
    defaultValues: shiftToFormDefaults(shift),
  });

  // Watch fields for conflict checking
  const employeeId = useWatch({ control: form.control, name: 'employeeId' });
  const shiftDate = useWatch({ control: form.control, name: 'shiftDate' });
  const startTime = useWatch({ control: form.control, name: 'startTime' });
  const endTime = useWatch({ control: form.control, name: 'endTime' });
  // The break feeds the day and week warnings (#2042).
  const breakMinutes = useWatch({ control: form.control, name: 'breakMinutes' });

  // Watch break times for auto-calculation
  const breakStart = useWatch({ control: form.control, name: 'breakStart' });
  const breakEnd = useWatch({ control: form.control, name: 'breakEnd' });

  // Auto-calculate break minutes from break start/end
  useEffect(() => {
    if (breakStart && breakEnd && breakStart !== '' && breakEnd !== '' && breakEnd > breakStart) {
      const [startH, startM] = breakStart.split(':').map(Number);
      const [endH, endM] = breakEnd.split(':').map(Number);
      const minutes = (endH * 60 + endM) - (startH * 60 + startM);
      if (minutes > 0) {
        form.setValue('breakMinutes', minutes);
      }
    }
  }, [breakStart, breakEnd, form]);

  // Debounce values for conflict check
  const debouncedEmployeeId = useDebounce(employeeId, 300);
  const debouncedShiftDate = useDebounce(shiftDate, 300);
  const debouncedStartTime = useDebounce(startTime, 300);
  const debouncedEndTime = useDebounce(endTime, 300);
  const debouncedBreakMinutes = useDebounce(breakMinutes, 300);

  // Check for conflicts when all required fields are filled
  useEffect(() => {
    // An open shift (#2040) has no employee to overlap.
    const canCheckConflicts =
      debouncedEmployeeId &&
      debouncedShiftDate &&
      debouncedStartTime &&
      debouncedEndTime &&
      // Validate time format (HH:MM)
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(debouncedStartTime) &&
      /^([01]\d|2[0-3]):([0-5]\d)$/.test(debouncedEndTime) &&
      // End time must be after start time
      debouncedEndTime > debouncedStartTime;

    if (canCheckConflicts) {
      checkConflicts.mutate({
        // An open shift (#2040) is checked as open: no overlap, the day limit only.
        ...(debouncedEmployeeId === OPEN_SHIFT_VALUE ? { open_shift: true } : { employee_id: debouncedEmployeeId }),
        shift_date: format(debouncedShiftDate, 'yyyy-MM-dd'),
        start_time: debouncedStartTime,
        end_time: debouncedEndTime,
        exclude_id: shift.id, // Exclude current shift from conflict check
        break_minutes:
          typeof debouncedBreakMinutes === 'number' && debouncedBreakMinutes >= 0
            ? debouncedBreakMinutes
            : undefined,
      });
    } else {
      // Clear stale conflict state when inputs are invalid
      checkConflicts.reset();
    }
    // Note: checkConflicts is intentionally excluded from deps to prevent infinite loop
    // The mutation object changes reference after each call, which would cause re-execution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedEmployeeId,
    debouncedShiftDate,
    debouncedStartTime,
    debouncedEndTime,
    debouncedBreakMinutes,
    shift.id,
  ]);

  // Reset form when dialog opens or shift changes
  useEffect(() => {
    if (open) {
      form.reset(shiftToFormDefaults(shift));
      checkConflicts.reset();
      setOptimisticLockError(false);
      setSaveCollision(null);
      setSingleEdit(false);
      setShowChoiceDialog(false);
      setPendingFormData(null);
      setUserRecurrenceType(null);
      setUserRepetitions(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shift]);

  // Handle form submission
  const handleSubmit = async (data: UpdateShiftFormData) => {
    setOptimisticLockError(false);
    setSaveCollision(null);
    if (!shift.id) {
      throw new Error('EditShiftDialog: shift.id is missing');
    }

    // For series shifts (non-exception), show the choice dialog
    if (seriesMode) {
      setPendingFormData(data);
      setShowChoiceDialog(true);
      return;
    }

    // For non-series shifts or exception shifts, update directly
    try {
      const request = toUpdateShiftRequest(data);
      await updateShift.mutateAsync({ id: shift.id, request });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      handleUpdateError(error, data.shiftDate);
    }
  };

  // Handle update error (#2119 F7): only the optimistic lock means "changed by someone
  // else"; an overlap or an absence is a collision of this edit and says so.
  const handleUpdateError = useCallback((error: unknown, shiftDate: Date | undefined) => {
    if (!(error instanceof ApiError) || error.status !== 409) return;
    const { code } = extractBackendError(error);
    if (code === 'SHIFT_CONFLICT' || code === 'EMPLOYEE_ABSENT') {
      setSaveCollision(collisionFromError(error, shiftDate ? format(shiftDate, 'yyyy-MM-dd') : undefined));
      return;
    }
    setOptimisticLockError(true);
  }, []);

  // Handle series update with chosen scope
  const handleSeriesUpdate = useCallback(
    async (scope: UpdateScope) => {
      if (!pendingFormData || !shift.id || !shift.pattern_id) {
        return;
      }

      try {
        // "This shift only" is the single-shift update (D3): it saves every field and marks
        // the shift as individually modified.
        if (scope === shifts_UpdateShiftSeriesRequest.update_scope.THIS_ONLY) {
          await updateShift.mutateAsync({ id: shift.id, request: toUpdateShiftRequest(pendingFormData) });
          setShowChoiceDialog(false);
          setPendingFormData(null);
          onOpenChange(false);
          onSuccess?.();
          return;
        }
        const request = buildSeriesUpdateRequest(
          scope,
          pendingFormData,
          shift.status,
          hasRecurrenceChanges ? { recurrenceType, repetitions } : null
        );

        await updateShiftSeries.mutateAsync({
          patternId: shift.pattern_id,
          shiftId: shift.id,
          request,
        });

        setShowChoiceDialog(false);
        setPendingFormData(null);
        onOpenChange(false);
        onSuccess?.();
      } catch (error) {
        handleUpdateError(error, pendingFormData.shiftDate);
        setShowChoiceDialog(false);
      }
    },
    [
      pendingFormData,
      shift.id,
      shift.pattern_id,
      shift.status,
      hasRecurrenceChanges,
      recurrenceType,
      repetitions,
      updateShift,
      updateShiftSeries,
      onOpenChange,
      onSuccess,
      handleUpdateError,
    ]
  );

  // Handle refresh after optimistic lock error
  const handleRefresh = () => {
    if (!shift.id) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: shiftKeys.detail(shift.id) });
    queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
    onOpenChange(false);
  };

  // Check if conflicts exist
  const hasConflicts = checkConflicts.data?.has_conflict === true;
  // The employee's first name for the availability line (#2041).
  const checkedEmployeeName = useMemo(() => {
    const employee = employeeList?.find((e) => e.id === debouncedEmployeeId);
    return employee?.firstname?.trim() || employee?.lastname?.trim() || '';
  }, [employeeList, debouncedEmployeeId]);

  // What the chosen status means for the employee (#2038 UX pass).
  const status = useWatch({ control: form.control, name: 'status' });
  const statusDescription: Record<string, string> = {
    [ShiftStatus.DRAFT]: i18n._({
      id: 'shifts.form.statusHelp.draft',
      message: 'Only planners see a draft. Publish the period to show it to the employee.',
    }),
    [ShiftStatus.PUBLISHED]: i18n._({
      id: 'shifts.form.statusHelp.published',
      message: 'The employee sees the shift in the app and can confirm it there.',
    }),
    [ShiftStatus.CONFIRMED]: i18n._({
      id: 'shifts.form.statusHelp.confirmed',
      message: 'Marks the shift as accepted by the employee, as if confirmed in the app.',
    }),
    [ShiftStatus.CANCELLED]: i18n._({
      id: 'shifts.form.statusHelp.cancelled',
      message: 'The shift stays visible, struck through, and no longer counts as planned time.',
    }),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" onOpenAutoFocus={focusFirstShiftField}>
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'shifts.edit.title', message: 'Edit Shift' })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {i18n._({
              id: 'shifts.edit.description',
              message: 'Edit shift details',
            })}
          </DialogDescription>
          {/* Series and Exception Indicators */}
          {(shift.pattern_id || shift.is_exception) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {shift.pattern_id && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1"
                  data-testid="series-badge"
                >
                  <Repeat className="h-3 w-3" />
                  {i18n._({
                    id: 'shifts.series.partOfSeries',
                    message: 'Part of a series',
                  })}
                </Badge>
              )}
              {shift.is_exception && (
                <Badge
                  variant="outline"
                  className="flex items-center gap-1 border-warning-on-surface/40 bg-warning-muted text-warning-on-surface"
                  data-testid="exception-badge"
                >
                  <Pencil className="h-3 w-3" />
                  {i18n._({
                    id: 'shifts.series.modified',
                    message: 'Individually modified',
                  })}
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Published Shift Warning */}
            {shift.status !== 'DRAFT' && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {i18n._({
                    id: 'shifts.edit.publishedWarning.title',
                    message: 'Published Shift',
                  })}
                </AlertTitle>
                <AlertDescription>
                  {i18n._({
                    id: 'shifts.edit.publishedWarning.notify',
                    message:
                      'The employee sees this shift in the app. A new date, time, group or employee sends a notification, and a confirmed shift must be confirmed again. Break and notes change silently.',
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* Optimistic Lock Error */}
            {optimisticLockError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {i18n._({
                    id: 'shifts.error.optimisticLock',
                    message: 'This shift was modified. Please refresh.',
                  })}
                </AlertTitle>
                <AlertDescription className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {i18n._({
                      id: 'shifts.error.optimisticLock.refresh',
                      message: 'Refresh',
                    })}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* The collision a save answered (#2119 F7) */}
            {saveCollision && !optimisticLockError && (
              <ShiftCollisionAlert
                collision={saveCollision}
                employeeName={checkedEmployeeName}
                totalDays={1}
              />
            )}

            {/* Conflict Warning */}
            {hasConflicts && !optimisticLockError && !saveCollision && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {i18n._({
                    id: 'shifts.conflict.warning',
                    message: 'Conflict Detected',
                  })}
                </AlertTitle>
                <AlertDescription>
                  {i18n._({
                    id: 'shifts.conflict.message',
                    message: 'This shift overlaps with an existing shift',
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* Availability (#2041): a warning only, the save stays enabled */}
            {!optimisticLockError && (
              <ShiftAvailabilityWarning
                availability={checkConflicts.data?.availability}
                employeeName={checkedEmployeeName}
              />
            )}

            {/* Working time warnings (#2042): advice only, save stays enabled. Hidden on an
                overlap: that save is refused, and its sums would count both shifts. */}
            <ShiftWarningLines warnings={hasConflicts ? undefined : checkConflicts.data?.warnings} />

            {/* Employee Select */}
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-1">
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.employee', message: 'Employee' })}
                    </FormLabel>
                    <HelpTip
                      data-testid="open-shift-help"
                      content={i18n._({
                        id: 'shifts.form.openShiftHelp',
                        message:
                          'Choose Open shift when nobody is assigned yet. After you publish it, the employees of its groups can ask for it in the app; the group lead or an admin approves one of them.',
                      })}
                    />
                  </div>
                  <FormControl>
                    <EmployeeSelect
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      id="employee-select"
                      aria-describedby="employee-error"
                      allowOpenShift
                      disabled={seriesMode}
                    />
                  </FormControl>
                  {field.value === OPEN_SHIFT_VALUE ? (
                    <p className="text-xs text-muted-foreground" data-testid="open-shift-caption">
                      {i18n._({
                        id: 'shifts.form.openShiftEditCaption',
                        message:
                          'Nobody is assigned. Employees of the shift’s groups can ask for it in the app while it is published.',
                      })}
                    </p>
                  ) : (
                    shift.is_open && (
                      <p className="text-xs text-muted-foreground" data-testid="open-shift-assign-caption">
                        {i18n._({
                          id: 'shifts.form.openShiftAssignCaption',
                          message:
                            'Saving gives the open shift to this employee directly. Open requests for it end and the employees who asked are notified.',
                        })}
                      </p>
                    )
                  )}
                  <FormMessage id="employee-error" />
                </FormItem>
              )}
            />

            {/* Date Picker */}
            <FormField
              control={form.control}
              name="shiftDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.date', message: 'Date' })}
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      disabled={seriesMode}
                      placeholder={i18n._({
                        id: 'common.selectDate',
                        message: 'Select date',
                      })}
                    />
                  </FormControl>
                  {/* D3 (#2119): date and employee belong to the series */}
                  {seriesMode && (
                    <FormDescription className="flex flex-wrap items-center gap-x-2" data-testid="series-locked-hint">
                      <span>
                        {i18n._({
                          id: 'shifts.form.seriesLockedHint',
                          message: 'Date and employee are set by the series.',
                        })}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-foreground underline underline-offset-2"
                        onClick={() => {
                          setSingleEdit(true);
                          setUserRecurrenceType(null);
                          setUserRepetitions(null);
                        }}
                        data-testid="edit-this-shift-only"
                      >
                        {i18n._({ id: 'shifts.form.editThisShiftOnly', message: 'Change this shift only' })}
                      </Button>
                    </FormDescription>
                  )}
                  {/* Changing one shift of a series on its own (D3, #2119), next to the fields it unlocked */}
                  {isSeriesShift && singleEdit && (
                    <FormDescription data-testid="single-edit-note">
                      {i18n._({
                        id: 'shifts.form.singleEditNote',
                        message:
                          'You are changing this shift on its own. It is marked as individually modified; the other shifts of the series stay as they are.',
                      })}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Time Fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.startTime', message: 'Start Time' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.endTime', message: 'End Time' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Break Minutes */}
            <FormField
              control={form.control}
              name="breakMinutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.breakMinutes', message: 'Break (min)' })}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Break Times */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="breakStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.breakStart', message: 'Break Start' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="breakEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'shifts.form.breakEnd', message: 'Break End' })}
                    </FormLabel>
                    <FormControl>
                      <TimePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Shift Type */}
            <FormField
              control={form.control}
              name="shiftType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.shiftType', message: 'Shift Type' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ShiftType.REGULAR}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.REGULAR} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftType.OVERTIME}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.OVERTIME} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftType.ON_CALL}>
                        <div className="flex items-center gap-2">
                          <ShiftTypeBadge type={ShiftType.ON_CALL} />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Status */}
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.status', message: 'Status' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ShiftStatus.DRAFT}>
                        <div className="flex items-center gap-2">
                          <ShiftStatusBadge status={ShiftStatus.DRAFT} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftStatus.PUBLISHED}>
                        <div className="flex items-center gap-2">
                          <ShiftStatusBadge status={ShiftStatus.PUBLISHED} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftStatus.CONFIRMED} disabled={employeeId === OPEN_SHIFT_VALUE}>
                        <div className="flex items-center gap-2">
                          <ShiftStatusBadge status={ShiftStatus.CONFIRMED} />
                        </div>
                      </SelectItem>
                      <SelectItem value={ShiftStatus.CANCELLED}>
                        <div className="flex items-center gap-2">
                          <ShiftStatusBadge status={ShiftStatus.CANCELLED} />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {status && statusDescription[status] && (
                    <FormDescription data-testid="status-help">{statusDescription[status]}</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Groups Multi-Select */}
            <FormField
              control={form.control}
              name="groupIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.groups', message: 'Groups' })}
                  </FormLabel>
                  <FormControl>
                    {/* `groupIds` carries a `.default([])`, so the INPUT value the
                        form holds is optional until validation fills it in. */}
                    <GroupMultiSelect
                      value={field.value ?? []}
                      onChange={field.onChange}
                      id="groups-select"
                      allowedGroupIds={writeScope.unrestricted ? undefined : writeScope.groupIds}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recurrence Settings (for series shifts) */}
            {seriesMode && !patternLoading && patternData && (
              <RecurrenceSettings
                recurrenceType={recurrenceType}
                repetitions={repetitions}
                onRecurrenceTypeChange={setUserRecurrenceType}
                onRepetitionsChange={setUserRepetitions}
                readOnly={false}
              />
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'shifts.form.notes', message: 'Notes' })}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={500} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hidden currentUpdatedAt field for optimistic locking */}
            <input type="hidden" {...form.register('currentUpdatedAt')} />

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {i18n._({ id: 'common.cancel', message: 'Cancel' })}
              </Button>
              <Button
                type="submit"
                disabled={
                  updateShift.isPending ||
                  updateShiftSeries.isPending ||
                  optimisticLockError ||
                  (seriesMode && patternLoading)
                }
              >
                {(updateShift.isPending || updateShiftSeries.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {i18n._({ id: 'common.save', message: 'Save' })}
              </Button>
            </div>
          </form>
        </Form>

        {/* Series Update Choice Dialog */}
        <SeriesUpdateChoiceDialog
          open={showChoiceDialog}
          onOpenChange={setShowChoiceDialog}
          onChoose={handleSeriesUpdate}
          isPending={updateShiftSeries.isPending}
          hasRecurrenceChanges={hasRecurrenceChanges}
          hasStatusChange={pendingFormData?.status !== undefined && pendingFormData.status !== shift.status}
        />
      </DialogContent>
    </Dialog>
  );
}
